import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  Mail, 
  MailOpen,
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  Archive,
  Trash2,
  Search,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';

const priorityConfig = {
  critical: { 
    label: 'Critical', 
    color: 'bg-red-500 text-white', 
    icon: AlertTriangle,
    bgColor: 'bg-red-50 border-red-200'
  },
  urgent: { 
    label: 'Urgent', 
    color: 'bg-orange-500 text-white', 
    icon: AlertCircle,
    bgColor: 'bg-orange-50 border-orange-200'
  },
  important: { 
    label: 'Important', 
    color: 'bg-blue-500 text-white', 
    icon: Info,
    bgColor: 'bg-blue-50 border-blue-200'
  },
  normal: { 
    label: 'Normal', 
    color: 'bg-gray-500 text-white', 
    icon: Mail,
    bgColor: 'bg-white border-gray-200'
  },
};

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadMessages = useCallback(async () => {
    try {
      const status = statusFilter !== 'all' ? statusFilter : null;
      const data = await clientPortalService.getMessages(status);
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleOpenMessage = async (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);
    
    // Mark as read if unread
    if (message.status === 'unread') {
      try {
        await clientPortalService.markMessageRead(message.id);
        setMessages(prev => 
          prev.map(m => m.id === message.id ? { ...m, status: 'read' } : m)
        );
      } catch (error) {
        console.error('Error marking message read:', error);
      }
    }
  };

  const handleArchiveMessage = async (messageId, e) => {
    e?.stopPropagation();
    try {
      await clientPortalService.updateMessageStatus(messageId, 'archived');
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message archived');
      if (selectedMessage?.id === messageId) {
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Error archiving message:', error);
      toast.error('Failed to archive message');
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesSearch = 
      message.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.body.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Group messages by priority for urgent display
  const urgentMessages = filteredMessages.filter(m => 
    m.priority === 'critical' || m.priority === 'urgent'
  );
  const otherMessages = filteredMessages.filter(m => 
    m.priority !== 'critical' && m.priority !== 'urgent'
  );

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="messages-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  const MessageCard = ({ message }) => {
    const priority = priorityConfig[message.priority] || priorityConfig.normal;
    const PriorityIcon = priority.icon;
    const isUnread = message.status === 'unread';
    
    return (
      <div 
        className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${priority.bgColor} ${
          isUnread ? 'border-l-4 border-l-amber-500' : ''
        }`}
        onClick={() => handleOpenMessage(message)}
        data-testid={`message-${message.id}`}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${isUnread ? 'bg-amber-100' : 'bg-gray-100'}`}>
            {isUnread ? (
              <Mail className="h-5 w-5 text-amber-600" />
            ) : (
              <MailOpen className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-semibold truncate ${isUnread ? 'text-gray-900' : 'text-gray-600'}`}>
                {message.subject}
              </h3>
              <Badge className={priority.color}>
                <PriorityIcon className="h-3 w-3 mr-1" />
                {priority.label}
              </Badge>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">{message.body}</p>
            <p className="text-xs text-gray-400 mt-2">
              {new Date(message.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={(e) => handleArchiveMessage(message.id, e)}
          >
            <Archive className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-6 w-6 text-amber-500" />
            Messages
            {unreadCount > 0 && (
              <Badge className="bg-amber-500 text-white ml-2">{unreadCount} new</Badge>
            )}
          </h1>
          <p className="text-sm text-gray-600 mt-1">Important updates from MAD Donuts</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-messages"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Messages</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
            <SelectItem value="read">Read</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Urgent Messages Section */}
      {urgentMessages.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="urgent-messages-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Urgent & Critical Messages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {urgentMessages.map(message => (
              <MessageCard key={message.id} message={message} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Other Messages */}
      <Card data-testid="messages-list">
        <CardHeader>
          <CardTitle className="text-lg">
            {urgentMessages.length > 0 ? 'Other Messages' : 'All Messages'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {otherMessages.length === 0 && urgentMessages.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No messages</p>
            </div>
          ) : otherMessages.length === 0 ? (
            <div className="text-center py-8">
              <Check className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <p className="text-gray-500">No other messages</p>
            </div>
          ) : (
            <div className="space-y-3">
              {otherMessages.map(message => (
                <MessageCard key={message.id} message={message} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedMessage && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge className={priorityConfig[selectedMessage.priority]?.color}>
                    {priorityConfig[selectedMessage.priority]?.label}
                  </Badge>
                  <DialogTitle className="text-lg">{selectedMessage.subject}</DialogTitle>
                  <DialogDescription className="sr-only"> dialog. Use the form below to continue.</DialogDescription>
                </div>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-gray-500">
                  {new Date(selectedMessage.created_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </p>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.body}</p>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleArchiveMessage(selectedMessage.id)}
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </Button>
                  <Button
                    className="bg-amber-500 hover:bg-amber-600"
                    onClick={() => setDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
